import { BCMSRecord, StateManager, useBCMSContext, useModel, useParams, useRoute, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import { CardOnFileMerchant, Merchant, RecurringPaymentMerchant, Transaction } from "model";
import React from "react";

import PaymentView from "components/shared/views/BaseRowView/components/PaymentView";
import { SVGAsset } from "res";

import "./styles.css";
/**
 * @function RecurringPaymentsView
 * @return {React.Component}
 * @exported true
 * @description The main component for RecurringPaymentsView
 */

export default function RecurringPaymentsView(props) {
  const globalController = useBCMSContext();
  BCMSRecord.globalControllerFor([ CardOnFileMerchant ], globalController);
  const data = { ...getDefaultData(), ...props.data ?? {} };
  const ui = { ...getDefaultUI(), ...props.ui ?? {} };

  const controller = (state, setState) => ({
    data: state.data,
    ui: state.ui,
  });

  return (
    <StateManager state={ { data, ui } } controller={ controller }>
      <PageContent/>
    </StateManager>
  );
}

export const getDefaultData = () => {
  const recurringPaymentMerchants = RecurringPaymentMerchant.initAll();
  const cardOnFileMerchants = CardOnFileMerchant.initAll()
  const transactions = Transaction.initAll();
  const merchants = Merchant.initAll();

  return {
    recurringPaymentMerchants,
    cardOnFileMerchants,
    merchants,
    transactions
  };
}

export const getDefaultUI = () => ({

});

function PageContent(props) {
  const { data, ui } = useStateManager();
  const { branding } = useModel();
  const options = branding.options ?? {};
  const hasAddToMerchant = options.push_to_merchant_chw23 === "true";

  const defaultCardID = options.include_debit_chw23  !== "false" ? "0" : "1";
  const cardID = useParams().cardID ?? defaultCardID;
  const navigate = useRoute();
  const { goStep, nextStep, prevStep, state } = useWorkflow() ?? {};

  const paymentClick = (merchant, cardOnFileMerchant) => () => {
    goStep(1, {  data: { ...data, merchantID: merchant.id, cardOnFileMerchant, cardOnFileSelectedTabType: "recurring" }, ui });
    //evt => navigate(`/home/merchantdetails/${ cardID }/${ transaction.merchant.id }/recurring`)
  }


  // Get all transactions with the same card id and type equal to recurring
  // Creating an array of "merchant" objects that matches the logic of the
  // CH21 code in order for the merchants to appear in the same
  // order in both demos.
  // IMPORTANT: The order of the merchants in the "model.xml" is the tie breaker between 2 transactions
  // with the same date. If "Big Box Inc." appears before "FuelStop",
  // then that will be the implicit deciding factor as to which transaction is shown first here.
  // In that case, "Big Box Inc." would be shown first, then "FuelStop".
  const merchantsWithTransaction = data.recurringPaymentMerchants
  .reduce((acc, recurringPaymentMechant) => {
    if(recurringPaymentMechant.card) {
      if(recurringPaymentMechant.card.id === cardID) acc.push(recurringPaymentMechant);
    }
    return acc;
  },[])
  .reduce((acc, recurringPaymentMechant) => {
    const transaction = data.transactions
    .sort((a, b) => {
      if(a.purchaseDate.isBefore(b.purchaseDate)) { return 1; }
      else if(a.purchaseDate.isAfter(b.purchaseDate)) { return -1; }
      else { return 0; }
    })
    .filter(item => {
      return item.card.id === cardID
      && item.merchant.id.toLowerCase() === recurringPaymentMechant.merchant.id
      && item.type.toLowerCase() === "recurring";
    }, [])
    [0];

    if(transaction !== undefined) {
      acc.push({
        cofMechant: recurringPaymentMechant,
        merchant: recurringPaymentMechant.merchant,
        transaction: transaction,
        recurringDateOffset: transaction.purchaseDate,
      });
    }
    return acc;
  }, [])
  .sort((a, b) => {
    if(a.recurringDateOffset.isBefore(b.recurringDateOffset)) { return 1; }
    else if(a.recurringDateOffset.isAfter(b.recurringDateOffset)) { return -1; }
    else { return 0; }
  })

  const items = merchantsWithTransaction
  .map(merchantsWithTransactionObj => {
    const merchant = merchantsWithTransactionObj.merchant;
    let showGreenStatus = false;
    if(merchant) {
      showGreenStatus = merchantsWithTransactionObj.cofMechant.status === "updated" || merchantsWithTransactionObj.cofMechant.status === "card added";
    }
    
    return (
      <PaymentView
        key={ `Payment_${ merchant.id }` }
        dataPoint={ `Recurring_Merchant_${ merchant.id }` }
        billerName={ merchant.name }
        leftIcon={ merchant.logo }
        date={ merchantsWithTransactionObj.transaction.purchaseDate.format("M d, Y") }
        amount={ merchantsWithTransactionObj.transaction.amount.format() }
        className={ hasAddToMerchant ? "show-status" : "" } // branding option will modify this to amount or a status
        statusText={ merchantsWithTransactionObj.cofMechant ? merchantsWithTransactionObj.cofMechant.status : "" }
        statusClassName={ showGreenStatus ? "green-status" : "" }
        onClick={ paymentClick(merchant, merchantsWithTransactionObj.cofMechant) }
      />
    );
  });

  return (
    <div className="recurring-payments-view">
      <p className="recurring-payments-view-text">The Recurring Payments list helps you keep tabs of merchants where you have stored your card information for subscriptions, recurring, repeating payments. <SVGAsset id="QuestionMarkCircleIcon" /></p>
      
      { items }
    </div>
  );
}